import { ChangeDetectionStrategy, Component } from '@angular/core';
import { EMAIL_SECURITY, EMAIL_SUPPORT } from '@app/config';
import { JsonLdService, MetaService } from '@app/core';
import { TuiButton } from '@taiga-ui/core';

interface SecurityItem {
  label: string;
  description: string;
}

interface SecurityGroup {
  label: string;
  items: SecurityItem[];
}

@Component({
  selector: 'app-trust',
  imports: [TuiButton],
  templateUrl: './trust.component.html',
  styleUrl: './trust.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrustComponent {
  protected readonly emailSecurity = EMAIL_SECURITY;
  protected readonly emailSupport = EMAIL_SUPPORT;
  protected readonly groups: SecurityGroup[] = [
    {
      label: 'Product security',
      items: [
        {
          label: 'SOC 2 Type II Compliance',
          description:
            "We take our customers' data security seriously. Our SOC 2 Type II compliance demonstrates our commitment to maintaining the highest standards of security and privacy. This certification ensures that we have implemented robust controls to protect your data and maintain its confidentiality, integrity, and availability.",
        },
        {
          label: 'Access Control',
          description:
            'We enforce strict access controls to ensure that only authorized personnel can access sensitive data. This includes role-based access controls (RBAC) and regular audits of access logs.',
        },
        {
          label: 'Source code protection',
          description:
            'Access to source code via your version control system is always encrypted using SSH and/or HTTPS.',
        },
      ],
    },
    {
      label: 'Data security',
      items: [
        {
          label: 'Data Encryption',
          description:
            'We use industry-standard encryption protocols to protect your data at rest and in transit. This ensures that your sensitive information is always secure and inaccessible to unauthorized users.',
        },
        {
          label: 'Data Backup',
          description:
            'Regular backups of your data are performed to ensure that you can recover from any data loss incidents. Our backup processes are designed to minimize downtime and ensure business continuity.',
        },
      ],
    },
    {
      label: 'Network security',
      items: [
        {
          label: 'TLS Encryption',
          description:
            'All data transmitted between your applications and our servers is protected using TLS 1.3 encryption, ensuring complete security during transit and preventing interception or tampering.',
        },
        {
          label: 'DDoS Protection',
          description:
            'Our infrastructure includes comprehensive DDoS protection to ensure your live updates remain available even during attack attempts, maintaining service reliability and uptime.',
        },
        {
          label: 'Network Monitoring',
          description:
            '24/7 network monitoring and threat detection systems continuously watch for suspicious activity, providing early warning and automatic response to potential security incidents.',
        },
      ],
    },
    {
      label: 'Application security',
      items: [
        {
          label: 'Code Signing',
          description:
            'All live updates can be cryptographically signed to ensure authenticity and integrity. Your applications verify these signatures before applying updates, preventing malicious code injection.',
        },
        {
          label: 'Vulnerability Scanning',
          description:
            'Regular automated security scans are performed on our platform and infrastructure to identify and remediate potential vulnerabilities before they can be exploited.',
        },
        {
          label: 'Secure Development',
          description:
            'Our development process follows secure coding practices with regular security reviews, penetration testing, and adherence to OWASP guidelines to prevent common vulnerabilities.',
        },
      ],
    },
    {
      label: 'Business security',
      items: [
        {
          label: 'Privacy Compliance',
          description:
            'We maintain compliance with GDPR, CCPA, and other privacy regulations, ensuring your user data is handled according to the highest privacy standards and legal requirements.',
        },
        {
          label: 'Business Continuity',
          description:
            'Comprehensive disaster recovery and business continuity plans ensure service availability even during unexpected events, with automated failover and redundant systems across multiple regions.',
        },
        {
          label: 'Incident Response',
          description:
            'Our dedicated security team maintains a 24/7 incident response capability with established procedures for detecting, containing, and resolving security incidents promptly.',
        },
      ],
    },
    {
      label: 'Physical security',
      items: [
        {
          label: 'Secure Data Centers',
          description:
            'Our infrastructure is hosted in tier-3 certified data centers with multi-layered physical security controls including biometric access, 24/7 surveillance, and environmental monitoring.',
        },
        {
          label: 'Hardware Security',
          description:
            'All hardware is secured with tamper-evident seals and regular inspections. Decommissioned equipment undergoes secure data destruction following industry best practices.',
        },
        {
          label: 'Environmental Controls',
          description:
            'Advanced fire suppression, climate control, and power management systems protect our infrastructure from environmental threats, ensuring consistent service availability.',
        },
      ],
    },
  ];

  constructor(
    private readonly metaService: MetaService,
    private readonly jsonLdService: JsonLdService,
  ) {
    this.metaService.setDescription(
      "Learn about Capawesome Cloud's commitment to security, privacy, and trust. Our enterprise-grade security measures protect your app updates and user data.",
    );
    this.metaService.setTitle('Trust & Security');
    this.metaService.setKeywords([
      'security',
      'compliance',
      'SOC 2',
      'GDPR',
      'data protection',
    ]);
    this.jsonLdService.setStructuredData(
      JsonLdService.buildWebPageSchema(
        'Trust & Security - Capawesome Cloud',
        "Learn about Capawesome Cloud's commitment to security, privacy, and trust.",
        'https://cloud.capawesome.io/trust/',
      ),
    );
  }
}
